/**
 * 园区数据排序、去重、筛选等辅助函数
 */

import {
  FilterCondition,
  NationalPark,
  Park,
  ParksDataFilter,
  PendingPark,
  ProvincialLikePark,
  ProvincialPark,
} from '../types';

/** 中文区域排序常用 locale */
const ZH_LOCALE = 'zh-Hans-CN';

export function isNationalPark(p: Park): p is NationalPark {
  return p.parkLevel === 'national';
}

export function isProvincialPark(p: Park): p is ProvincialPark {
  return p.parkLevel === 'provincial';
}

export function isPendingPark(p: Park): p is PendingPark {
  return p.parkLevel === 'pending';
}

export function isProvincialLikePark(p: Park): p is ProvincialLikePark {
  return isProvincialPark(p) || isPendingPark(p);
}

/** 按园区等级统计数量（侧栏卡片、Hook 共用） */
export function countParksByLevel(parks: Park[]): {
  national: number;
  provincial: number;
  pending: number;
} {
  return {
    national: parks.filter((p) => p.parkLevel === 'national').length,
    provincial: parks.filter((p) => p.parkLevel === 'provincial').length,
    pending: parks.filter((p) => p.parkLevel === 'pending').length,
  };
}

/**
 * 将 Hook 层筛选状态转换为通用 FilterCondition（未选维度不写入，保持 AND 语义）
 */
export function parksDataFilterToFilterCondition(
  f: ParksDataFilter,
): FilterCondition {
  const c: FilterCondition = {
    parkLevel: f.level,
  };
  if (f.level === 'national') {
    if (f.nationalProjectTypes && f.nationalProjectTypes.length > 0) {
      c.nationalProjectTypeLabels = [...f.nationalProjectTypes];
    }
    if (f.nationalDocking) {
      c.nationalDocking = f.nationalDocking;
    }
  }
  if (f.level === 'provincial' || f.level === 'pending') {
    const tier = f.comprehensiveEnergyConsumption?.trim();
    if (tier) {
      c.comprehensiveEnergyConsumptionTier = tier;
    }
    if (f.greenElectricityScales && f.greenElectricityScales.length > 0) {
      c.greenElectricityScales = [...f.greenElectricityScales];
    }
    if (f.provincialLeadingIndustries && f.provincialLeadingIndustries.length > 0) {
      c.provincialLeadingIndustryLabels = [...f.provincialLeadingIndustries];
    }
    if (f.level === 'pending' && f.pendingGrossRegionalProduct) {
      c.pendingGrossRegionalProductTier = f.pendingGrossRegionalProduct;
    }
  }
  return c;
}

/** CSV「对接情况」归一为是否视作已对接（用于 已对接/未对接 筛选） */
export function parkDockingIsPositive(status: string): boolean {
  const s = (status || '').trim();
  if (!s) return false;
  if (/未对接|待对接|未\s*对接|无对接/.test(s)) return false;
  if (/已对接|对接/.test(s)) return true;
  return false;
}

/** 工作管理中有对接记录的园区，在园区查询中展示为已对接 */
export function applyDockingStatusFromRecords(
  parks: Park[],
  dockedParkIds: ReadonlySet<string>,
): Park[] {
  if (dockedParkIds.size === 0) return parks;
  return parks.map((park) =>
    dockedParkIds.has(park.id) ? { ...park, dockingStatus: '已对接' } : park,
  );
}

function nationalProjectLabelsMatch(park: NationalPark, labels: readonly string[]): boolean {
  for (const label of labels) {
    const L = label.trim();
    if (!L) continue;
    const ok = park.projectTypes.some(
      (pt) =>
        pt === L ||
        pt.includes(L) ||
        L.includes(pt),
    );
    if (!ok) return false;
  }
  return true;
}

function provincialLeadingLabelsMatch(
  park: ProvincialLikePark,
  labels: readonly string[],
): boolean {
  for (const label of labels) {
    const L = label.trim();
    if (!L) continue;
    const ok = park.leadingIndustries.some(
      (li) => li === L || li.includes(L) || L.includes(li),
    );
    if (!ok) return false;
  }
  return true;
}

function matchesGrossRegionalProductTier(value: number, tier: string): boolean {
  if (!Number.isFinite(value) || value <= 0) return false;
  if (tier === '300亿元以下') return value < 300;
  if (tier === '300亿~600亿元') return value >= 300 && value < 600;
  if (tier === '600亿~1000亿元') return value >= 600 && value < 1000;
  if (tier === '1000亿~2000亿元') return value >= 1000 && value < 2000;
  if (tier === '2000亿元以上') return value >= 2000;
  return true;
}

/**
 * 按省份名称排序（中文拼音/笔画由 locale 决定）
 */
export function sortParksByProvince(parks: Park[]): Park[] {
  return [...parks].sort((a, b) =>
    a.province.localeCompare(b.province, ZH_LOCALE),
  );
}

/**
 * 从园区列表中提取某一字段的去重值列表（string 字段；布尔/数值字段请自行转换或扩展）
 */
export function getUniqueValues<T extends Park>(
  parks: readonly T[],
  field: keyof T,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parks) {
    const v = p[field];
    if (typeof v !== 'string' || v === '') {
      continue;
    }
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out.sort((a, b) => a.localeCompare(b, ZH_LOCALE));
}

function matchesKeyword(park: Park, keyword: string): boolean {
  const k = keyword.trim().toLowerCase();
  if (!k) {
    return true;
  }
  const base = [
    park.name,
    park.province,
    park.city,
    park.district,
    park.dockingStatus,
  ];
  if (isNationalPark(park)) {
    base.push(
      park.file,
      park.range,
      park.buildingCycle,
      park.investmentBracket,
      park.basicSituation,
      ...park.projectTypes,
      ...park.technicalAdvantages,
    );
  } else if (isProvincialLikePark(park)) {
    base.push(
      park.file,
      park.comprehensiveEnergyConsumption,
      park.basicSituation,
      park.failReason,
      park.remark,
      ...park.leadingIndustries,
    );
  }
  const haystack = base.join(' ').toLowerCase();
  return haystack.includes(k);
}

/**
 * 按条件筛选园区（条件未设置的维度不参与过滤）
 */
export function filterParksByCondition(
  parks: Park[],
  condition: FilterCondition,
): Park[] {
  const {
    parkLevel = 'all',
    provinces,
    cities,
    districts,
    dockingStatuses,
    nationalDocking,
    nationalProjectTypeLabels,
    nationalProjectTypeContains,
    technicalAdvantageKeywords,
    comprehensiveEnergyConsumptionTier,
    greenElectricityScales,
    pendingGrossRegionalProductTier,
    leadingIndustryContains,
    provincialLeadingIndustryLabels,
    keyword,
  } = condition;

  return parks.filter((park) => {
    if (parkLevel === 'national' && !isNationalPark(park)) {
      return false;
    }
    if (parkLevel === 'provincial' && !isProvincialPark(park)) {
      return false;
    }
    if (parkLevel === 'pending' && !isPendingPark(park)) {
      return false;
    }

    if (provinces && provinces.length > 0 && !provinces.includes(park.province)) {
      return false;
    }
    if (cities && cities.length > 0 && !cities.includes(park.city)) {
      return false;
    }
    if (districts && districts.length > 0 && !districts.includes(park.district)) {
      return false;
    }
    if (
      dockingStatuses &&
      dockingStatuses.length > 0 &&
      !dockingStatuses.includes(park.dockingStatus)
    ) {
      return false;
    }

    if (isNationalPark(park)) {
      if (nationalDocking) {
        const positive = parkDockingIsPositive(park.dockingStatus);
        if (nationalDocking === '已对接' && !positive) return false;
        if (nationalDocking === '未对接' && positive) return false;
      }
      if (
        nationalProjectTypeLabels &&
        nationalProjectTypeLabels.length > 0 &&
        !nationalProjectLabelsMatch(park, nationalProjectTypeLabels)
      ) {
        return false;
      }
      if (nationalProjectTypeContains) {
        const needle = nationalProjectTypeContains.trim();
        if (
          needle &&
          !park.projectTypes.some(
            (pt) => pt === needle || pt.includes(needle),
          )
        ) {
          return false;
        }
      }
      if (technicalAdvantageKeywords && technicalAdvantageKeywords.length > 0) {
        const blob = park.technicalAdvantages.join(' ');
        const ok = technicalAdvantageKeywords.some((w) =>
          blob.includes(w.trim()),
        );
        if (!ok) {
          return false;
        }
      }
    }

    if (isProvincialLikePark(park)) {
      const energyTier = park.comprehensiveEnergyConsumption.trim();
      const tierFilter = comprehensiveEnergyConsumptionTier?.trim();
      if (tierFilter && energyTier !== tierFilter) {
        return false;
      }
      if (
        provincialLeadingIndustryLabels &&
        provincialLeadingIndustryLabels.length > 0 &&
        !provincialLeadingLabelsMatch(park, provincialLeadingIndustryLabels)
      ) {
        return false;
      }
      if (leadingIndustryContains) {
        const needle = leadingIndustryContains.trim();
        if (
          needle &&
          !park.leadingIndustries.some(
            (li) => li === needle || li.includes(needle),
          )
        ) {
          return false;
        }
      }
      if (
        greenElectricityScales &&
        greenElectricityScales.length > 0 &&
        !greenElectricityScales.includes(park.greenElectricityScale)
      ) {
        return false;
      }
    }

    if (
      isPendingPark(park) &&
      pendingGrossRegionalProductTier &&
      !matchesGrossRegionalProductTier(
        park.grossRegionalProduct ?? 0,
        pendingGrossRegionalProductTier,
      )
    ) {
      return false;
    }

    if (keyword && !matchesKeyword(park, keyword)) {
      return false;
    }

    return true;
  });
}
